import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useSettingsSelection } from '@/hooks/use-settings-selection';
import { useNow } from '@/hooks/use-now';
import { useMeQuery } from '@/api/user';
import {
  useListServiceAccountsQuery,
  accountsSelector,
  useListOrganisationsQuery,
  organisationByIdSelector,
  useNewServiceAccountAccessKeyMutation,
  useNewServiceAccountMutation,
  useDeleteServiceAccountMutation,
  useDeleteServiceAccountAccessKeyMutation,
  useUpdateServiceAccountMutation,
} from '@/api/organisation';
import { Loading } from '@/components/loading';
import { OrganisationServiceAccounts } from '@/components/organisation-service-accounts';
import { NewServiceAccount, ServiceAccount, AccessKeySecret } from '@/types';
import { AccessKeyCreateModal } from '@/components/access-keys/access-key-create-modal';

const SettingsOrganisationServiceAccounts = () => {
  const now = useNow();
  const navigate = useNavigate();
  const { id } = useSettingsSelection() as any;
  const { isLoading: isLoadingOrganisation } = useListOrganisationsQuery();
  const organisation = useSelector((state: any) =>
    organisationByIdSelector(state, id),
  );
  const { data: me, isLoading: isLoadingMe } = useMeQuery();
  const { isLoading } = useListServiceAccountsQuery(id);
  const accounts = useSelector(accountsSelector(id));
  const [createNewAccessKey, { isLoading: isCreatingKey }] =
    useNewServiceAccountAccessKeyMutation();
  const [createNewServiceAccount] = useNewServiceAccountMutation();
  const [deleteServiceAccount] = useDeleteServiceAccountMutation();
  const [deleteServiceAccountAccessKey] =
    useDeleteServiceAccountAccessKeyMutation();
  const [updateServiceAccount] = useUpdateServiceAccountMutation();

  const [creatingForAccount, setCreatingForAccount] = useState<
    ServiceAccount | undefined
  >();
  const [newAccessKey, setNewAccessKey] = useState<
    undefined | AccessKeySecret
  >();
  const onNewAccessKey = useCallback((account: ServiceAccount) => {
    setCreatingForAccount(account);
  }, []);
  const onCancelCreate = useCallback(() => {
    setCreatingForAccount(undefined);
  }, []);
  const onCreateWithScopes = useCallback(
    async (name: string, scopes: string[], expiresAt: string | null) => {
      if (!creatingForAccount) return;
      setCreatingForAccount(undefined);
      const accessKey = await createNewAccessKey({
        accountId: creatingForAccount.id,
        organisationId: id,
        name,
        scopes,
        expiresAt,
      }).unwrap();
      setNewAccessKey(accessKey);
    },
    [id, creatingForAccount, createNewAccessKey],
  );
  const onDismissNewAccessKey = useCallback(() => {
    setNewAccessKey(undefined);
  }, [setNewAccessKey]);

  const onNewServiceAccount = useCallback(
    async (account: NewServiceAccount) => {
      await createNewServiceAccount({
        organisationId: id,
        account: account,
      }).unwrap();
    },
    [createNewServiceAccount, id],
  );

  const onDeleteServiceAccount = useCallback(
    async (account: ServiceAccount) => {
      await deleteServiceAccount({
        accountId: account.id,
        organisationId: id,
      }).unwrap();
    },
    [id, deleteServiceAccount],
  );

  const onDeleteServiceAccountAccessKey = useCallback(
    async (accountId: number, keyId: string) => {
      await deleteServiceAccountAccessKey({
        accountId,
        organisationId: id,
        keyId,
      }).unwrap();
    },
    [id, deleteServiceAccountAccessKey],
  );

  const onUpdateDescription = useCallback(
    async (accountId: number, description: string | null) => {
      await updateServiceAccount({
        organisationId: id,
        accountId,
        description,
      }).unwrap();
    },
    [id, updateServiceAccount],
  );

  const onClickKey = useCallback(
    (keyId: string) => {
      // Find which account owns this key.
      const account = accounts.find((a) => a.keys.some((k) => k.id === keyId));
      if (account) {
        navigate(
          `/settings/organisation/${id}/service-accounts/${account.id}/keys/${keyId}`,
        );
      }
    },
    [id, accounts, navigate],
  );

  const onManageConnectors = useCallback(
    (account: ServiceAccount) => {
      navigate(
        `/settings/organisation/${id}/service-accounts/${account.id}/connectors`,
      );
    },
    [id, navigate],
  );

  if (
    !isLoading &&
    !isLoadingOrganisation &&
    !isLoadingMe &&
    accounts &&
    organisation &&
    me
  ) {
    return (
      <>
        {creatingForAccount && (
          <AccessKeyCreateModal
            onCancel={onCancelCreate}
            onCreate={onCreateWithScopes}
            isCreating={isCreatingKey}
          />
        )}
        <OrganisationServiceAccounts
          key={id}
          organisationId={id}
          now={now}
          me={me}
          accounts={accounts}
          onNewAccessKey={onNewAccessKey}
          onDismissNewAccessKey={onDismissNewAccessKey}
          newAccessKey={newAccessKey}
          onCreate={onNewServiceAccount}
          onDelete={onDeleteServiceAccount}
          onDeleteKey={onDeleteServiceAccountAccessKey}
          onClickKey={onClickKey}
          onManageConnectors={onManageConnectors}
          onUpdateDescription={onUpdateDescription}
        />
      </>
    );
  } else {
    return <Loading />;
  }
};

export { SettingsOrganisationServiceAccounts };
